import { useEffect, useLayoutEffect, useRef } from 'react';

import { useLocation } from 'react-router-dom';

// A fresh navigation (typed URL, link, search result) has no browser scroll
// restoration, so an offset present when the app first mounts was made by the
// visitor while the prerendered page was waiting to hydrate, or by a text
// fragment (#:~:text=) jump. Resetting it would yank them back to the top.
function keepsInitialScroll(): boolean {
  if (window.scrollY <= 0) return false;
  const [navigation] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
  return navigation?.type === 'navigate';
}

export function ScrollToTop() {
  const { key, hash } = useLocation();
  const initialMountPending = useRef(true);

  // Take scroll control away from the browser. Otherwise, on reload the browser
  // restores the previous scroll position *after* async content + lazy images
  // grow the page height — which raced (and beat) the scroll-to-top below,
  // leaving pages opening mid-way down. With 'manual', only our own
  // window.scrollTo runs, so every page reliably opens at the top.
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Keyed on location.key, not pathname: re-clicking the link for the page you
  // are already on pushes a new history entry with the same pathname, and that
  // still has to send you back to the top. Layout effect, so the jump happens
  // before paint — a remounted page must never flash at the old scroll offset.
  //
  // An in-page anchor (#…) is the one navigation that owns its own scroll
  // target — see IntroScreen on /buy/pre-approval — so leave it alone.
  //
  // The initial-mount exception is consumed exactly once, even when a hash
  // returns early, so it can never apply to a later navigation. Reloads and
  // back/forward keep the reset that guards against restoration races.
  useLayoutEffect(() => {
    const initialMount = initialMountPending.current;
    initialMountPending.current = false;
    if (hash) return;
    if (initialMount && keepsInitialScroll()) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [key, hash]);

  return null;
}
